import { Edge, WorkflowNode } from '@ply-io/ply-front-shared-utilities';

type DistributiveOmit<T, K extends keyof T> = T extends unknown ? Omit<T, K> : never;

/**
 * Make the properties received in T non-nullable
 * for example:
 * type A = { a: string | null; b: number: c?: boolean; d?: string };
 * type B = NonNullableProperties<A, 'a' | 'c'>;
 * // B = { a: string; b: number: c: boolean; d?: string };
 * @param T The type to make non-nullable properties
 * @param Keys The keys of the properties to make non-nullable
 * @returns The type with the non-nullable properties
 */
export type NonNullableProperties<T, Keys extends keyof T> = DistributiveOmit<T, Keys> & {
	[K in Keys]-?: NonNullable<T[K]>;
};

type ReactCommonProps = React.HTMLAttributes<HTMLElement> & React.ClassAttributes<HTMLElement>;
export type ExtendedReactElement<P> = ReactCommonProps & P;

interface GraphAffectedChanges<T> {
	created: T[];
	updated: T[];
	deleted: T[];
}

export type StepGraphAffectedChanges = {
	nodes: GraphAffectedChanges<WorkflowNode>;
	edges: GraphAffectedChanges<Edge>;
};

export const isStepGraphAffectedChanges = (changes: unknown): changes is StepGraphAffectedChanges => {
	const casted = changes as StepGraphAffectedChanges;
	return (
		typeof casted === 'object' &&
		'nodes' in casted &&
		'edges' in casted &&
		'created' in casted.nodes &&
		'updated' in casted.nodes &&
		'deleted' in casted.nodes &&
		'created' in casted.edges &&
		'updated' in casted.edges &&
		'deleted' in casted.edges
	);
};
